import React, { useEffect, useRef } from "react";
import { PropTypes } from "prop-types";
import TextField from "./style/ERPSBaseTextFieldStyles";
import { withERPSTextFieldActionValidator } from "../../hoc/withERPSTextFieldActionValidator";
import LoadingIcon from "./ERPSLoadingIcon";
import { FormControl } from "../../primitives";

function ERPSBaseTextField(props) {
  const {
    id,
    type,
    value,
    label,
    required,
    onChange,
    size,
    className,
    inputProps,
    items,
    select,
    SelectProps,
    error,
    isDisabled,
    InputLabelProps,
    onBlur,
    onInput,
    hasMargin,
    allowDecimal = false,
    loading = false,
    listDisabled = false,
    onKeyPress,
    helperText,
    fullWidth = false,
    defaultValue,
    resetWhenRender = false
  } = props;

  const hasResetRef = useRef(false);

  // Reset value to defaultValue only when component renders (mounts) with resetWhenRender=true
  // This does not trigger when dropdown option changes
  useEffect(() => {
    if (!resetWhenRender) {
      // Reset the flag when resetWhenRender becomes false, so it can reset again if needed
      hasResetRef.current = false;
      return;
    }

    if (resetWhenRender && !hasResetRef.current && defaultValue !== undefined && onChange) {
      hasResetRef.current = true;
      onChange({ target: { value: defaultValue } });
    }
    // Below comment is used to suppress the unnecessary build warning. Do not remove.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetWhenRender]);

  const handleKeyDown = (e) => {
    // Handle Arrow UP and DOWN key if number field
    if (
      type === "number" &&
      (e.keyCode === 38 ||
        e.keyCode === 40 ||
        e.key === "e" ||
        e.key === "E" ||
        e.key === "+" ||
        e.key === "-" ||
        (e.key === "." && allowDecimal === false))
    ) {
      e.preventDefault();
    }
  };

  const handleWheel = (e) => {
    if(type === "number") {
      // Stop value update on mouse scroll for number field
      e.target.blur();
    }
  }

  // Global solution for MUI select field shrink issue
  // For select fields, shrink behavior should be controlled based on value presence
  // to prevent label overlap with placeholder/options
  const getInputLabelProps = () => {
    if (select) {
      // If shrink is explicitly provided, respect it
      if (InputLabelProps?.shrink !== undefined) {
        return InputLabelProps;
      }
      // Otherwise, apply enhanced shrink logic for select fields
      // This ensures label shrinks correctly on all interactions (click, keyboard navigation)
      return {
        ...InputLabelProps,
        shrink: !!value || undefined,
      };
    }
    // For non-select fields, use the provided InputLabelProps as-is
    return InputLabelProps;
  };

  // Determine FormControl styling based on fullWidth prop
  // fullWidth=true: Field expands to fill available space (for fields with width: 100% in CSS)
  // fullWidth=false (default): Field uses its CSS-defined fixed width
  const formControlSx = fullWidth
    ? { flex: '1 1 auto', maxWidth: '100%' } // Flexible: grows up to max-width
    : { flex: '0 0 auto', maxWidth: '100%' }; // Fixed: doesn't grow, respects CSS width

  return (
    <FormControl sx={formControlSx}>
      <TextField
        select={select}
        error={error}
        id={id}
        type={type}
        value={value}
        label={label}
        onChange={onChange}
        onBlur={onBlur}
        className={className}
        required={required === "true"}
        size={size}
        InputProps={inputProps}
        SelectProps={SelectProps}
        disabled={isDisabled || loading}
        InputLabelProps={getInputLabelProps()}
        inputProps={inputProps}
        onInput={onInput}
        hasMargin={hasMargin}
        onKeyDown={handleKeyDown}
        onWheel={handleWheel}
        onKeyPress={onKeyPress}
        helperText={helperText}
        defaultValue={defaultValue}
        sx={{
          maxWidth: { xs: '100%', sm: '100%' }
        }}
      >
        {items &&
          items.map((option) => (
            <option
              key={option.value}
              disabled={listDisabled}
              value={option.value}
            >
              {option.label}
            </option>
          ))}
      </TextField>
    </FormControl>
  );
}

ERPSBaseTextField.propTypes = {
  label: PropTypes.string.isRequired,
  size: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  resetWhenRender: PropTypes.bool,
};

export default withERPSTextFieldActionValidator(ERPSBaseTextField);
